import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CLabel,
  CPagination,
  CRow,
} from "@coreui/react";
import moment from "moment";
import { useEffect, useState } from "react";
import Datetime from "react-datetime";
import { useHistory, useParams } from "react-router-dom";
import { getItem, getList } from "../../api/generics";
import { formatToCurrency } from "../../currency/format";
import { PYG } from "../../currency/available-currencies";
import TreasuryAccount, {
  ENTRY_ACTIONS,
  MOVEMENT_TYPES,
  TreasuryAccountEntry,
} from "../../models/treasury";
import { getRoutePath } from "../../routes";
import { formatDateTime } from "../../utils/dates";
import { SUCCESS } from "../../utils/constants/tags";
import { warningAlert } from "../utils/messages";

const ITEMS_PER_PAGE = 50;

const TreasuryAccountHistory = () => {
  const fields = [
    { key: "createdAt", label: "Fecha" },
    { key: "action", label: "Acción" },
    { key: "detail", label: "Detalle" },
    { key: "amount", label: "Monto", _classes: ["text-right"] },
    { key: "balanceAfter", label: "Saldo resultante", _classes: ["text-right"] },
    { key: "userName", label: "Usuario" },
  ];

  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [account, setAccount] = useState<TreasuryAccount | undefined>(
    undefined
  );
  const [entries, setEntries] = useState<TreasuryAccountEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [startDate, setStartDate] = useState<string | moment.Moment>("");
  const [endDate, setEndDate] = useState<string | moment.Moment>("");

  const fetchAccount = async () => {
    const accountStatus = await getItem<TreasuryAccount>(
      `/treasury/accounts/${id}/`
    );
    if (accountStatus.status === SUCCESS) {
      setAccount(accountStatus.data);
    } else {
      warningAlert(accountStatus.detail ?? "Error desconocido");
    }
  };

  const fetchEntries = async (page: number) => {
    // CPagination "corrige" la pagina activa a `pages` cuando hay menos
    // paginas que la actual; con 0 resultados eso es la pagina 0.
    page = Math.max(1, page);
    setLoading(true);
    const params = new Map<string, string | undefined>();
    if (moment.isMoment(startDate)) {
      params.set("start_date", startDate.toISOString());
    }
    if (moment.isMoment(endDate)) {
      params.set("end_date", endDate.toISOString());
    }
    const entriesStatus = await getList<TreasuryAccountEntry>(
      `/treasury/accounts/${id}/entries/`,
      ITEMS_PER_PAGE,
      ITEMS_PER_PAGE * (page - 1),
      params
    );
    if (entriesStatus.status === SUCCESS && entriesStatus.data) {
      setEntries(entriesStatus.data.items);
      setTotalPages(
        Math.max(1, Math.ceil(entriesStatus.data.count / ITEMS_PER_PAGE))
      );
      setCurrentPage(page);
    } else {
      warningAlert(entriesStatus.detail ?? "Error desconocido");
    }
    setLoading(false);
  };

  const onDateChange =
    (setter: (value: string | moment.Moment) => void) =>
    (value: string | moment.Moment) => {
      setter(moment.isMoment(value) && value.isValid() ? value : "");
    };

  useEffect(() => {
    fetchAccount();
    fetchEntries(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <CRow>
      <CCol lg="12">
        <CCard>
          <CCardHeader className="d-flex flex-row mb-3">
            <div className="p-2">
              <CButton
                color="secondary"
                onClick={() => history.push(getRoutePath("treasuryAccounts"))}
              >
                <i className="fa fa-arrow-left"></i>
              </CButton>
            </div>
            <div className="p-2">
              <h3>Historial: {account?.name}</h3>
            </div>
            <div className="p-2 ml-auto">
              <h4>
                Saldo actual:{" "}
                <b>{formatToCurrency(Number(account?.balance ?? 0), PYG)}</b>
              </h4>
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow className="mb-3">
              <CCol md={1}>
                <CLabel className="mt-2">Desde:</CLabel>
              </CCol>
              <CCol md={3}>
                <Datetime
                  onChange={onDateChange(setStartDate)}
                  value={startDate}
                  locale="es/PY"
                  dateFormat="DD/MM/YYYY"
                  timeFormat="HH:mm"
                  closeOnSelect={true}
                ></Datetime>
              </CCol>
              <CCol md={1}>
                <CLabel className="mt-2">Hasta:</CLabel>
              </CCol>
              <CCol md={3}>
                <Datetime
                  onChange={onDateChange(setEndDate)}
                  value={endDate}
                  locale="es/PY"
                  dateFormat="DD/MM/YYYY"
                  timeFormat="HH:mm"
                  closeOnSelect={true}
                ></Datetime>
              </CCol>
              <CCol>
                <CButton color="primary" onClick={() => fetchEntries(1)}>
                  Buscar
                </CButton>
              </CCol>
            </CRow>
            <CDataTable
              noItemsView={<h2 className="text-center">Sin Resultados</h2>}
              fields={fields}
              items={entries}
              striped
              border
              loading={loading}
              responsive
              scopedSlots={{
                createdAt: (item: TreasuryAccountEntry) => (
                  <td>{formatDateTime(new Date(item.createdAt))}</td>
                ),
                action: (item: TreasuryAccountEntry) => (
                  <td>{ENTRY_ACTIONS.get(item.action) ?? item.action}</td>
                ),
                detail: (item: TreasuryAccountEntry) => (
                  <td>
                    {item.movementType ? (
                      <b>
                        {MOVEMENT_TYPES.get(item.movementType) ??
                          item.movementType}
                        {item.movementId ? ` #${item.movementId}` : ""}:{" "}
                      </b>
                    ) : (
                      <></>
                    )}
                    {item.obs ?? item.movementDescription ?? "-"}
                  </td>
                ),
                amount: (item: TreasuryAccountEntry) => (
                  <td
                    className={
                      "text-right " +
                      (Number(item.amount) < 0 ? "text-danger" : "text-success")
                    }
                  >
                    {formatToCurrency(Number(item.amount), PYG)}
                  </td>
                ),
                balanceAfter: (item: TreasuryAccountEntry) => (
                  <td className="text-right">
                    <b>{formatToCurrency(Number(item.balanceAfter), PYG)}</b>
                  </td>
                ),
                userName: (item: TreasuryAccountEntry) => (
                  <td>{item.userName ?? "-"}</td>
                ),
              }}
            />
            <div className="d-flex justify-content-center">
              <CPagination
                pages={totalPages}
                activePage={currentPage}
                onActivePageChange={(page: number) => fetchEntries(page)}
                className={totalPages < 2 ? "d-none" : ""}
              />
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

export default TreasuryAccountHistory;
